import { useEffect, useId, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useUser } from '@/providers/auth0';
import { COLLECTION_MECHANIC } from '@/models/collection-mechanic/collection-mechanic.types';
import Button from '@/components/inputs/button';
import {
  SIZE as BUTTON_SIZE,
  VARIANT as BUTTON_VARIANT,
  SIZE,
} from '@/components/inputs/button/button.types';
import Icon from '@/components/data-display/icon';
import { VARIANT as ICON_VARIANT } from '@/components/data-display/icon/icon.types';
import Accordion from '@/components/data-display/accordion';
import Flyout from '@/components/flyout';
import Image from '@/components/data-display/image';
import PromotionalBanner from '@/components/integrated/promotional-banner';
import Tooltip from '@/components/utilities/tooltip';
import FavouriteButton from '@/modules/favourite-button/favourite-button';
import ShopNowButton from '@/modules/shop-now-button';
import { useFeatureTogglesContext } from '@/context/feature-toggles/feature-toggles-context';
import { FEATURE_TOGGLE_LIST } from '@/context/feature-toggles/feature-toggles.types';
import {
  EVENT_NAME,
  NAV_ELEMENT,
  USER_ACTION,
} from '@/utils/track-event/track-event.types';
import trackEvent from '@/utils/track-event/track-event';
import { interpolateMicrocopy } from '@/utils/interpolate-microcopy';
import useHandleFavourites from '@/hooks/use-handle-favourites';
import useScrollToTop from '@/hooks/use-scroll-to-top';
import AviosTimeline from '@/modules/avios-timeline';
import { useRouter } from 'next/router';
import { findUpToOrFrom } from '@/components/integrated/partner-tile/partner-tile';
import { Heading } from '@alto-avios/alto-ui';
import type { PartnerInfoProperties } from './partner-info.types';
import {
  formattedCtaLabelNotShopNow,
  formattedCtaLabelShopNow,
  getCtaLinkWithParameters,
  showPromotionBannerByCollectionMethod,
  sortTabs,
} from './partner-info.utils';
import styles from './partner-info.module.scss';

export const ACCORDION_DELAY = 1500;

const PartnerInfo = ({
  slug,
  mechanicId,
  externalReferences,
  isUserBlocked,
  collinsonId,
  ctaLabel,
  ctaLabelTrack,
  purchaseConditions,
  rates,
  subtitle,
  name,
  breadcrumbs,
  terms,
  title,
  about,
  card,
  hero,
  tabs,
  aviosTimeline,
  favouritesLabels,
  partnerPromotionalBannerProperties,
  isUserLoggedIn,
  shopNowLoggedOutCtaLabel,
  setShowError, // eslint-disable-next-line sonarjs/cognitive-complexity
}: PartnerInfoProperties) => {
  const router = useRouter();
  const { user } = useUser();

  const featuresToggles = useFeatureTogglesContext();
  const isFavouriteFeatureActive = !featuresToggles.includes(
    FEATURE_TOGGLE_LIST.NO_FAVS,
  );

  const breadcrumbsActive = featuresToggles.includes(
    FEATURE_TOGGLE_LIST.PDP_BREADCRUMBS,
  );

  const aviosTimelineEnabled = featuresToggles.includes(
    FEATURE_TOGGLE_LIST.TEMP_AVIOS_TIMELINE,
  );

  const shopNowCollectionMethods = [COLLECTION_MECHANIC.EStore];
  const showFavouriteButton = isFavouriteFeatureActive && user;

  const purchaseConditionAccordion = useId();
  const aboutAccordion = useId();
  const { reference, scrollToTop } = useScrollToTop();

  const [activeAccordion, setActiveAccordion] = useState<string>('');

  const collectionMethods = Object.keys(tabs);
  const { cm: urlCollectionMethod } = router.query;

  const validCollectionMethod =
    urlCollectionMethod &&
    Object.values(COLLECTION_MECHANIC).includes(
      urlCollectionMethod as COLLECTION_MECHANIC,
    );

  const initialCollectionMethod =
    (validCollectionMethod && (urlCollectionMethod as COLLECTION_MECHANIC)) ||
    (collectionMethods.find(
      method => method === COLLECTION_MECHANIC.EStore,
    ) as COLLECTION_MECHANIC) ||
    (collectionMethods[0] as COLLECTION_MECHANIC);

  const [activeCollectionMethod, setActiveCollectionMethod] =
    useState<COLLECTION_MECHANIC>(initialCollectionMethod);

  const [ctaLink, setCtaLink] = useState(
    card[activeCollectionMethod]?.cta.link ?? '',
  );

  const sortedTabs = Object.entries(tabs).sort(sortTabs);

  useEffect(() => {
    let cancel = false;

    const delayedAccordionActivation = async () => {
      await new Promise<void>(resolve => {
        setTimeout(() => {
          if (!cancel) {
            setActiveAccordion(purchaseConditionAccordion);
            resolve();
          }
        }, ACCORDION_DELAY);
      });
    };

    delayedAccordionActivation();

    return () => {
      cancel = true;
      setActiveAccordion('');
    };
  }, [purchaseConditionAccordion, slug]);

  useEffect(() => {
    if (sortedTabs.length === 0 || urlCollectionMethod) return;
    const [[collectionName]] = sortedTabs;
    setActiveCollectionMethod(collectionName as COLLECTION_MECHANIC);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs]);

  const isLoginRequired = card[activeCollectionMethod]?.loginRequired;
  const forceLoginUser = !isUserLoggedIn && isLoginRequired;
  const { asPath } = useRouter();

  useEffect(() => {
    const link = getCtaLinkWithParameters({
      card,
      activeCollectionMethod,
      user,
      isLoginRequired,
    });
    if (link) setCtaLink(link);
  }, [activeCollectionMethod, card, user, isLoginRequired]);

  const handleCLOGAEvent = () => {
    if (activeCollectionMethod === COLLECTION_MECHANIC.CLO) {
      const eventData = user
        ? {
            event: EVENT_NAME.CARD_LINKED,
            user_action: USER_ACTION.RETAILER_REGISTER_CARD,
          }
        : {
            event: EVENT_NAME.ONLINE_LOGINBUTTON,
            user_action: USER_ACTION.RETAILER_LOG_IN,
          };

      trackEvent(eventData);
    }
  };

  const handleChangeCollectionMethod = (
    collectionMethod: COLLECTION_MECHANIC,
  ) => {
    const query =
      collectionMethod === COLLECTION_MECHANIC.CLO
        ? { cm: collectionMethod }
        : {};
    const pathname = router.asPath?.split('?')[0];

    router.replace(
      { query: { slug, ...query } },
      { pathname, query },
      {
        shallow: true,
      },
    );

    setActiveCollectionMethod(collectionMethod);
  };

  const collectionMethodTitle =
    tabs[activeCollectionMethod]?.tabTitle?.toLocaleLowerCase();

  const displayShopNowButton = shopNowCollectionMethods.includes(
    activeCollectionMethod,
  );

  const { isFavourite, toggleFavourite } = useHandleFavourites();

  const isFavouriteId = useMemo(
    () => isFavourite(mechanicId),
    [isFavourite, mechanicId],
  );

  const { collectText, partnerCategory } = breadcrumbs;

  const breadcrumbFirstLevelUrl =
    activeCollectionMethod === COLLECTION_MECHANIC.CLO
      ? '/collect-on-card'
      : '/retailers';

  const ctaTextOverwrite = card[activeCollectionMethod]?.cta.ctaTextOverwrite;

  const ctaLabelNotShopNow = useMemo(
    () =>
      formattedCtaLabelNotShopNow(
        card[activeCollectionMethod]?.cta.text,
        ctaTextOverwrite,
        isUserLoggedIn,
        shopNowLoggedOutCtaLabel,
        isLoginRequired,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      ctaTextOverwrite,
      isUserLoggedIn,
      shopNowLoggedOutCtaLabel,
      isLoginRequired,
    ],
  );

  const ctaLabelForShopNow = useMemo(
    () =>
      formattedCtaLabelShopNow(
        ctaLabel,
        isUserLoggedIn,
        ctaTextOverwrite,
        shopNowLoggedOutCtaLabel,
      ),
    [ctaLabel, isUserLoggedIn, ctaTextOverwrite, shopNowLoggedOutCtaLabel],
  );

  const partnerTileRates = (collectionMethod: COLLECTION_MECHANIC) => {
    const prefix = findUpToOrFrom(card[collectionMethod]?.rate ?? '');
    const rateWithoutPrefix =
      prefix && card[collectionMethod].rate?.split(prefix)[1];
    return (
      <>
        <div className={classNames(styles['partner-info__rate'])}>
          {card[collectionMethod]?.rate && (
            <div className={classNames(styles['partner-info__rate'])}>
              <p
                className={classNames(styles['partner-info__rate'], {
                  [styles['partner-info__rate--highlighted']]:
                    card[collectionMethod]?.wasRate,
                })}
              >
                {card[collectionMethod]?.wasRate && (
                  <Icon
                    className={styles['partner-info__rate--highlighted-icon']}
                    variant={ICON_VARIANT.ArrowUp}
                  />
                )}
                {prefix ? (
                  <span>
                    <span className={styles['partner-info__rate-prefix']}>
                      {prefix}
                    </span>
                    <span
                      className={styles['partner-info__rate-highlighted-rate']}
                    >
                      {rateWithoutPrefix}
                    </span>
                  </span>
                ) : (
                  card[collectionMethod]?.rate
                )}
              </p>
            </div>
          )}
        </div>
        {card[collectionMethod]?.wasRate && (
          <p className={classNames(styles['partner-info__was-rate'])}>
            {card[collectionMethod]?.rate && card[collectionMethod]?.wasRate}
          </p>
        )}
      </>
    );
  };

  const collectionMethodTiles = (
    <>
      {sortedTabs.map(([collectionMethod, content]) => {
        const { tabTitle } = content;

        return (
          <Button
            key={collectionMethod}
            variant={BUTTON_VARIANT.Unstyled}
            className={classNames(styles['partner-info__card-info__tile'], {
              [styles['partner-info__card-info__tile__active']]:
                activeCollectionMethod === collectionMethod,
            })}
            onClick={() =>
              handleChangeCollectionMethod(
                collectionMethod as COLLECTION_MECHANIC,
              )
            }
            onKeyDown={element => {
              if (element.key === 'Enter' || element.key === ' ') {
                handleChangeCollectionMethod(
                  collectionMethod as COLLECTION_MECHANIC,
                );
              }
            }}
            role="tab"
            tabIndex={0}
            aria-selected={activeCollectionMethod === collectionMethod}
          >
            {tabTitle && <p>{tabTitle}</p>}
            {partnerTileRates(collectionMethod as COLLECTION_MECHANIC)}
          </Button>
        );
      })}
    </>
  );
  const partnerTileContent = (
    <>
      {card[activeCollectionMethod]?.isSpeedyAwarding && (
        <Tooltip description={card.speedyAwardText}>
          <p className={styles['partner-info__card-speedy']}>
            <Icon
              variant={ICON_VARIANT.Speedy}
              aria-label={card.speedyAwardText}
              aria-hidden={false}
            />
          </p>
        </Tooltip>
      )}

      <Image
        className={styles['partner-info__card-logo']}
        src={card.logo.src}
        width={150}
        height={60}
        alt={card.logo.alt}
        priority
      />
      {Object.entries(tabs).length > 1 ? (
        <div className={styles['partner-info__card-info']}>
          {collectionMethodTiles}
        </div>
      ) : (
        <div className={styles['partner-info__card-info__single']}>
          {partnerTileRates(activeCollectionMethod)}
        </div>
      )}
      <div className={styles['partner-info__card-cta']}>
        {displayShopNowButton ? (
          <ShopNowButton
            slug={slug}
            isUserBlocked={isUserBlocked}
            collinsonId={collinsonId}
            merchantId={externalReferences[activeCollectionMethod]}
            ctaLabel={isUserBlocked ? ctaLabel : ctaLabelForShopNow}
            ctaLabelTrack={ctaLabelTrack}
            collectionMethodType={activeCollectionMethod}
            setErrorCallback={setShowError}
            className={styles['partner-info__card-cta-button-primary']}
          />
        ) : (
          <Button
            as="link"
            target={
              forceLoginUser
                ? '_self'
                : card[activeCollectionMethod]?.cta.target
            }
            href={
              forceLoginUser
                ? `/api/auth/login?returnTo=${encodeURIComponent(asPath)}`
                : ctaLink
            }
            className={styles['partner-info__card-cta-button-primary']}
            title={ctaLabelNotShopNow}
            onClick={handleCLOGAEvent}
          >
            {ctaLabelNotShopNow}
          </Button>
        )}
        {showFavouriteButton && (
          <FavouriteButton
            isFavourited={!!isFavouriteId}
            handleFavourite={() =>
              toggleFavourite(
                !isFavouriteId,
                mechanicId,
                isFavouriteId as number,
              )
            }
            addLabel={favouritesLabels.addToFavourites}
            removeLabel={favouritesLabels.removeFromFavourites}
            addedLabel={favouritesLabels.addedToFavourites}
            removedLabel={favouritesLabels.removedFromFavourites}
          />
        )}
      </div>
      {terms[activeCollectionMethod]?.text && (
        <Flyout label={terms.openLabel}>
          <Flyout.Trigger
            className={styles['partner-info__card-terms']}
            variant={BUTTON_VARIANT.Text}
            size={BUTTON_SIZE.Small}
            GAEventReference={`${collectionMethodTitle} ${NAV_ELEMENT.TERMS_LINK}`}
          >
            {terms.openLabel}
          </Flyout.Trigger>

          <Flyout.Content ref={reference}>
            <h3
              className={styles['partner-info__terms-title']}
              dangerouslySetInnerHTML={{
                __html: terms[activeCollectionMethod]?.title as string,
              }}
            />

            <div
              className={styles['partner-info__terms-body']}
              dangerouslySetInnerHTML={{
                __html: terms[activeCollectionMethod]?.text as string,
              }}
            />

            <Button
              variant={BUTTON_VARIANT.Text}
              size={BUTTON_SIZE.xSmall}
              trailingIcon={ICON_VARIANT.ChevronUp}
              onClick={() => scrollToTop()}
              title={terms.backToTopLabel}
            >
              {terms.backToTopLabel}
            </Button>
          </Flyout.Content>
        </Flyout>
      )}
    </>
  );

  return (
    <section className={styles['partner-info']}>
      <div className={styles['partner-info__hero']}>
        <Image
          className={styles['partner-info__hero-image']}
          src={hero.src}
          alt={hero.alt}
          ariaHidden
          fill
          priority
          quality={100}
        />
      </div>
      <div className={styles['partner-info__container']}>
        <div className={`${styles['partner-info__card']} ${styles.card}`}>
          {partnerTileContent}
        </div>
        <div className={styles['partner-info__body']}>
          {breadcrumbsActive && partnerCategory && (
            <div className={styles.breadcrumbs}>
              <Button
                className={styles.breadcrumbs__link}
                as="link"
                href={breadcrumbFirstLevelUrl}
                variant={BUTTON_VARIANT.Text}
                size={SIZE.Medium}
                trailingIcon={ICON_VARIANT.ChevronRight}
                title={`${collectText} ${collectionMethodTitle}`}
              >
                {`${collectText} ${collectionMethodTitle}`}
              </Button>
              <Button
                className={styles.breadcrumbs__link}
                as="link"
                href={`/${partnerCategory.url}`}
                variant={BUTTON_VARIANT.Text}
                size={SIZE.Medium}
                trailingIcon={ICON_VARIANT.ChevronRight}
                title={partnerCategory.name}
              >
                {partnerCategory.name}
              </Button>
              <span className={styles.breadcrumbs__partner} title={name}>
                {name}
              </span>
            </div>
          )}

          <div className={styles['partner-info__heading']}>
            <Heading size="lg">
              {interpolateMicrocopy(title, {
                collectionMethod: collectionMethodTitle as string,
              })}
            </Heading>
          </div>
          {partnerPromotionalBannerProperties?.bannerEnabled &&
            showPromotionBannerByCollectionMethod(
              activeCollectionMethod,
              partnerPromotionalBannerProperties?.collectionMethod,
            ) && (
              <PromotionalBanner
                {...partnerPromotionalBannerProperties}
                className={styles['partner-promotional-banner']}
              />
            )}
          {aviosTimelineEnabled && <AviosTimeline {...aviosTimeline} />}
          <Accordion
            as="h2"
            isOpen={activeAccordion}
            title={subtitle}
            onToggle={setActiveAccordion}
            id={purchaseConditionAccordion}
            className={styles['partner-info__accordion']}
            GAEventReference={NAV_ELEMENT.MERCHANT_NUTSHELL_ACCORDION}
          >
            <div className={styles['partner-info__inner-accordion']}>
              {purchaseConditions[activeCollectionMethod]?.highlighted && (
                <div
                  className={
                    styles['partner-info__purchase-conditions-highlight']
                  }
                  dangerouslySetInnerHTML={{
                    __html: purchaseConditions[activeCollectionMethod]
                      ?.highlighted as string,
                  }}
                />
              )}

              {rates[activeCollectionMethod]?.value.length > 0 && (
                <table
                  className={styles['partner-info__purchase-conditions-rates']}
                  cellPadding={0}
                  cellSpacing={0}
                >
                  <thead>
                    <tr>
                      <th colSpan={2}>
                        {rates[activeCollectionMethod]?.title}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {rates[activeCollectionMethod]?.value.map(tieredRate => (
                      <tr key={tieredRate.name}>
                        <td>{tieredRate.name}</td>

                        <td
                          className={
                            styles[
                              'partner-info__purchase-conditions-rates-value'
                            ]
                          }
                        >
                          {tieredRate.rate}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {purchaseConditions[activeCollectionMethod]?.text && (
                <div
                  dangerouslySetInnerHTML={{
                    __html: purchaseConditions[activeCollectionMethod]
                      ?.text as string,
                  }}
                />
              )}
            </div>
          </Accordion>

          {about.text && (
            <Accordion
              as="h2"
              isOpen={activeAccordion}
              title={about.title}
              onToggle={setActiveAccordion}
              id={aboutAccordion}
              className={styles['partner-info__accordion']}
              GAEventReference={NAV_ELEMENT.MERCHANT_NUTSHELL_ACCORDION}
            >
              <div
                className={styles['partner-info__inner-accordion']}
                dangerouslySetInnerHTML={{
                  __html: about.text,
                }}
              />
            </Accordion>
          )}
        </div>
      </div>
    </section>
  );
};

export default PartnerInfo;
